import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, TrendingUp, Home, DollarSign, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [reconScan, setReconScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanLoading, setScanLoading] = useState(false);

  useEffect(() => {
    fetchProperty();
    fetchReconScan();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReconScan = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}/recon-scan`);
      setReconScan(response.data);
    } catch (error) {
      // No recon scan found - expected for new properties
    }
  };

  const generateReconScan = async () => {
    setScanLoading(true);
    try {
      const response = await axios.post(`${API}/properties/${id}/recon-scan`);
      setReconScan(response.data);
    } catch (error) {
      console.error('Error generating recon scan:', error);
    } finally {
      setScanLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8" data-testid="loading-container">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="p-6 lg:p-8" data-testid="property-not-found">
        <p>Property not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="property-detail-page">
      <Button
        onClick={() => navigate('/search')}
        variant="ghost"
        className="mb-6"
        data-testid="back-button"
      >
        <ArrowLeft className="mr-2" size={20} />
        Back to Search
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Card className="border-border overflow-hidden" data-testid="property-main-card">
            <div className="h-96 bg-secondary relative">
              <img
                src={property.image_url || 'https://images.unsplash.com/photo-1659434565863-905f8033d217?crop=entropy&cs=srgb&fm=jpg&q=85'}
                alt={property.address}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 flex gap-2">
                {property.is_distressed && (
                  <Badge className="bg-destructive" data-testid="badge-distressed">Distressed</Badge>
                )}
                {property.section8_eligible && (
                  <Badge className="bg-accent" data-testid="badge-section8">Section 8</Badge>
                )}
                {property.is_vacant && (
                  <Badge className="bg-orange-500" data-testid="badge-vacant">Vacant</Badge>
                )}
              </div>
            </div>
            <CardContent className="p-6">
              <h1 className="text-3xl font-black mb-2" data-testid="property-address">{property.address}</h1>
              <p className="text-muted-foreground mb-6" data-testid="property-location">
                {property.city}, {property.state} {property.zip_code}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div data-testid="detail-price">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Price</p>
                  <p className="text-2xl font-mono font-bold text-primary">${property.price.toLocaleString()}</p>
                </div>
                <div data-testid="detail-bedrooms">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Bedrooms</p>
                  <p className="text-2xl font-mono font-bold">{property.bedrooms || 'N/A'}</p>
                </div>
                <div data-testid="detail-bathrooms">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Bathrooms</p>
                  <p className="text-2xl font-mono font-bold">{property.bathrooms || 'N/A'}</p>
                </div>
                <div data-testid="detail-sqft">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Square Feet</p>
                  <p className="text-2xl font-mono font-bold">{property.sqft?.toLocaleString() || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div data-testid="detail-type">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Type</p>
                  <p className="font-medium">{property.property_type}</p>
                </div>
                <div data-testid="detail-year">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Year Built</p>
                  <p className="font-medium">{property.year_built || 'N/A'}</p>
                </div>
                <div data-testid="detail-lot">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Lot Size</p>
                  <p className="font-medium">{property.lot_size?.toLocaleString() || 'N/A'} sqft</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {reconScan && (
            <Card className="border-border" data-testid="recon-scan-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="text-accent" />
                  Recon Scan Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-6">
                  <div data-testid="recon-arv">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">ARV</p>
                    <p className="text-2xl font-mono font-bold text-accent">${reconScan.arv.toLocaleString()}</p>
                  </div>
                  <div data-testid="recon-rehab">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Rehab Est.</p>
                    <p className="text-2xl font-mono font-bold">${reconScan.rehab_estimate.toLocaleString()}</p>
                  </div>
                  <div data-testid="recon-rent">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Rent Est.</p>
                    <p className="text-2xl font-mono font-bold">${reconScan.rental_estimate.toLocaleString()}/mo</p>
                  </div>
                  <div data-testid="recon-grade">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Deal Grade</p>
                    <p className={`text-2xl font-mono font-bold deal-grade-${reconScan.deal_grade.toLowerCase()[0]}`}>
                      {reconScan.deal_grade}
                    </p>
                  </div>
                  <div data-testid="recon-offer">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Rec. Offer</p>
                    <p className="text-2xl font-mono font-bold text-primary">${reconScan.recommended_offer.toLocaleString()}</p>
                  </div>
                  <div data-testid="recon-motivation">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Seller Motivation</p>
                    <Badge variant="outline" className="text-base">{reconScan.seller_motivation}</Badge>
                  </div>
                </div>
                <div data-testid="recon-analysis">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">AI Analysis</p>
                  <p className="text-sm leading-relaxed">{reconScan.analysis}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-4 space-y-4">
          <Card className="border-border" data-testid="actions-card">
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!reconScan ? (
                <Button
                  onClick={generateReconScan}
                  disabled={scanLoading}
                  className="w-full glow-accent bg-accent hover:bg-accent/90"
                  size="lg"
                  data-testid="btn-generate-scan"
                >
                  {scanLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2" size={20} />
                      Generate Recon Scan
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={generateReconScan}
                  disabled={scanLoading}
                  className="w-full"
                  variant="outline"
                  data-testid="btn-regenerate-scan"
                >
                  Regenerate Scan
                </Button>
              )}
              <Button
                onClick={() => navigate('/underwriting')}
                className="w-full bg-primary hover:bg-primary/90"
                size="lg"
                data-testid="btn-run-underwriting"
              >
                <TrendingUp className="mr-2" size={20} />
                Run Underwriting
              </Button>
              <Button
                variant="outline"
                className="w-full"
                size="lg"
                data-testid="btn-make-offer"
              >
                <DollarSign className="mr-2" size={20} />
                Make Offer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;